/** The broker the backend sends this browser to; for EMQX, its public WebSocket host and port and the MQTT token to use. */
export interface MqttBrokerTarget {
    broker: 'aedes' | 'emqx';
    url?: string;
    port?: number;
    token?: string;
}

/** Where the MQTT client connects and the token it signs in with; `ssl` is set for EMQX on 443, its public wss listener. */
export interface MqttEndpoint {
    url: string;
    port: number;
    token: string;
    ssl?: boolean;
}

/** Why the browser moved between brokers, or kept its broker after a failed check. */
export type BrokerSwitchReason = 'backend' | 'unreachable' | 'check-failed';

/** How long a broker check may take before the browser keeps its current broker. */
export const BROKER_CHECK_TIMEOUT_MS = 5_000;
/** How often the browser asks again while on EMQX, which also renews its MQTT token before it lapses. */
export const BROKER_CHECK_INTERVAL_MS = 5 * 60_000;
/** The first wait before a failed check is tried again; it doubles up to {@link BROKER_CHECK_INTERVAL_MS}. */
export const BROKER_CHECK_RETRY_MS = 30_000;
/** Failed checks in a row retried before the browser waits for its next sign-in. */
export const BROKER_CHECK_RETRIES = 5;
/** How long the browser may fail to connect to EMQX before it falls back to Aedes. */
export const EMQX_CONNECT_FALLBACK_MS = 30_000;
/** How long a browser that fell back ignores the backend's EMQX answer. */
export const EMQX_FALLBACK_COOLDOWN_MS = 15 * 60_000;
/** How long a switch found during a PTT waits before the browser checks again. */
export const BROKER_SWITCH_RETRY_MS = 5_000;

/** What the broker choice needs from the app, injected so it is testable. */
export interface BrokerSelectorDeps {
    /** Asks the backend which broker to use (`GET core/mqtt/connection`). */
    fetchTarget(): Promise<MqttBrokerTarget>;
    /** The Aedes endpoint from the app's environment. */
    aedes(): MqttEndpoint;
    /** The token a browser signed in by header (Teams) sends; empty for a cookie sign-in. */
    bearerToken(): string;
    /** Points the MQTT client at `endpoint`, reconnecting it when `reconnect` is set. */
    apply(endpoint: MqttEndpoint, reconnect: boolean): void;
    /** True while the client is connected to `endpoint` itself, not to the broker it is leaving. */
    isOnEndpoint(endpoint: MqttEndpoint): boolean;
    /** True while a PTT is being sent or played, when a switch would cut it. */
    isPttBusy(): boolean;
    /** Catches sync up after a move at `movedAt` to `endpoint`, once the client has reached it; see `catchUpSyncAndReport`. */
    catchUpSync(movedAt: number, endpoint: MqttEndpoint): Promise<void>;
    /** Reports a move between brokers, or a failed check while on EMQX. */
    report(event: { fromBroker: 'aedes' | 'emqx', toBroker: 'aedes' | 'emqx', reason: BrokerSwitchReason, pttBusy?: boolean, pttDeferredMs?: number }): void;
}

/**
 * Chooses the browser's MQTT broker: EMQX when the backend says so with a token (or a header sign-in), else Aedes.
 * On EMQX it rechecks every {@link BROKER_CHECK_INTERVAL_MS} to renew the token, and falls back if EMQX is unreachable. */
export class MqttBrokerSelector {
    private endpoint?: MqttEndpoint;
    private active = false;
    private failedChecks = 0;
    private cooldownUntil = 0;
    private nextCheck?: ReturnType<typeof setTimeout>;
    private fallback?: ReturnType<typeof setTimeout>;
    private deferredSince?: number;
    /** Counts stops, so a check still in flight when the browser signs out never applies to the next sign-in. */
    private stops = 0;

    constructor(private readonly deps: BrokerSelectorDeps, private readonly now: () => number = Date.now) {}

    /** The EMQX endpoint the browser is on, or undefined on Aedes. */
    get emqx(): MqttEndpoint | undefined {
        return this.endpoint;
    }

    /**
     * Resolves the broker for a sign-in. Before the first connect the caller connects once it settles; for a client
     * already connected (an account switch) a change of broker is a move, which reconnects, reports and catches up. */
    async start(firstConnect = true): Promise<void> {
        this.active = true;
        this.failedChecks = 0;
        await this.check(firstConnect);
    }

    /** Stops checking and returns to Aedes for the next sign-in. */
    stop(): void {
        this.stops++;
        this.active = false;
        this.endpoint = undefined;
        this.deferredSince = undefined;
        this.clearTimers();
    }

    /** Called on each connection change: while on EMQX and not connected to it, falls back to Aedes after {@link EMQX_CONNECT_FALLBACK_MS}. */
    onConnected(connected: boolean): void {
        if (this.endpoint && connected && this.deps.isOnEndpoint(this.endpoint)) {
            clearTimeout(this.fallback);
            this.fallback = undefined;
            return;
        }
        if (!connected) this.watchEmqxReachable();
    }

    /**
     * Falls back to Aedes, ignoring EMQX for {@link EMQX_FALLBACK_COOLDOWN_MS}, if the client has not reached EMQX within
     * {@link EMQX_CONNECT_FALLBACK_MS}: a first connect that never succeeds, or a token EMQX refuses, ends here. */
    private watchEmqxReachable(): void {
        if (!this.active || !this.endpoint) return;
        this.fallback ??= setTimeout(() => {
            this.fallback = undefined;
            if (!this.active || !this.endpoint || this.deps.isOnEndpoint(this.endpoint)) return;
            this.cooldownUntil = this.now() + EMQX_FALLBACK_COOLDOWN_MS;
            this.switchTo(undefined, 'unreachable', false);
        }, EMQX_CONNECT_FALLBACK_MS);
    }

    private async check(initial = false): Promise<void> {
        clearTimeout(this.nextCheck);
        this.nextCheck = undefined;
        const stops = this.stops;
        const superseded = () => !this.active || stops !== this.stops;
        let target: MqttBrokerTarget;
        try {
            target = await this.fetchWithTimeout();
            if (superseded()) return;
            this.failedChecks = 0;
        } catch (e) {
            if (superseded()) return;
            if (isMissingEndpoint(e)) {
                target = { broker: 'aedes' };
            } else {
                console.warn('PubSubService: broker check failed; keeping the current broker', e);
                if (this.endpoint) this.deps.report({ fromBroker: 'emqx', toBroker: 'emqx', reason: 'check-failed' });
                this.retryFailedCheck();
                return;
            }
        }
        const next = this.endpointFor(target);
        if (next && this.endpoint && next.url === this.endpoint.url && next.port === this.endpoint.port) {
            this.endpoint = next;
            this.deferredSince = undefined;
            this.deps.apply(next, false);
            this.schedule(BROKER_CHECK_INTERVAL_MS);
            return;
        }
        if (!next && !this.endpoint) {
            this.deferredSince = undefined;
            this.scheduleCooldownEnd();
            return;
        }
        if (!initial && this.deps.isPttBusy()) {
            this.deferredSince ??= this.now();
            this.schedule(BROKER_SWITCH_RETRY_MS);
            return;
        }
        this.switchTo(next, 'backend', initial);
    }

    /** The EMQX endpoint to use for `target`, or undefined for Aedes, including while cooling down or without a token. */
    private endpointFor(target: MqttBrokerTarget): MqttEndpoint | undefined {
        const token = target.token || this.deps.bearerToken();
        if (target.broker !== 'emqx' || !target.url || !token || this.now() < this.cooldownUntil) return undefined;
        const port = target.port ?? 443;
        return { url: target.url, port, token, ssl: port === 443 };
    }

    /** Moves to `next` (undefined for Aedes); a move found while signing in needs no reconnect or catch-up. */
    private switchTo(next: MqttEndpoint | undefined, reason: BrokerSwitchReason, initial: boolean): void {
        const fromBroker = this.endpoint ? 'emqx' : 'aedes';
        const toBroker = next ? 'emqx' : 'aedes';
        this.endpoint = next;
        this.deps.apply(next ?? this.deps.aedes(), !initial);
        clearTimeout(this.fallback);
        this.fallback = undefined;
        const pttDeferredMs = this.deferredSince === undefined ? undefined : this.now() - this.deferredSince;
        this.deferredSince = undefined;
        if (!initial) {
            this.deps.report({ fromBroker, toBroker, reason, pttBusy: this.deps.isPttBusy(), ...(pttDeferredMs === undefined ? {} : { pttDeferredMs }) });
            const movedAt = this.now();
            this.deps.catchUpSync(movedAt, next ?? this.deps.aedes()).catch(e => console.error('PubSubService: sync catch-up failed', e));
        }
        if (next) {
            this.schedule(BROKER_CHECK_INTERVAL_MS);
            this.watchEmqxReachable();
        } else {
            this.clearTimers();
            this.scheduleCooldownEnd();
        }
    }

    /** While a fallback cooldown runs, checks again when it ends, so the browser returns to EMQX without a new sign-in. */
    private scheduleCooldownEnd(): void {
        const remaining = this.cooldownUntil - this.now();
        if (this.active && remaining > 0) this.schedule(remaining);
    }

    /**
     * Tries a failed check again, waiting {@link BROKER_CHECK_RETRY_MS} and doubling, at most {@link BROKER_CHECK_RETRIES}
     * times; on EMQX it then keeps the regular interval, since its token must still be renewed. */
    private retryFailedCheck(): void {
        if (this.failedChecks >= BROKER_CHECK_RETRIES) {
            if (this.endpoint) this.schedule(BROKER_CHECK_INTERVAL_MS);
            return;
        }
        const delay = Math.min(BROKER_CHECK_RETRY_MS * 2 ** this.failedChecks, BROKER_CHECK_INTERVAL_MS);
        this.failedChecks++;
        this.schedule(delay);
    }

    private schedule(delayMs: number): void {
        clearTimeout(this.nextCheck);
        this.nextCheck = setTimeout(() => {
            this.nextCheck = undefined;
            this.check().catch(e => console.error('PubSubService: broker check failed', e));
        }, delayMs);
    }

    private async fetchWithTimeout(): Promise<MqttBrokerTarget> {
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            return await Promise.race([
                this.deps.fetchTarget(),
                new Promise<never>((_resolve, reject) => {
                    timer = setTimeout(() => reject(new Error('broker check timed out')), BROKER_CHECK_TIMEOUT_MS);
                }),
            ]);
        } finally {
            clearTimeout(timer);
        }
    }

    private clearTimers(): void {
        clearTimeout(this.nextCheck);
        clearTimeout(this.fallback);
        this.nextCheck = undefined;
        this.fallback = undefined;
    }
}

/** Whether a failed check means the backend has no broker endpoint yet (404), which means Aedes. */
function isMissingEndpoint(e: unknown): boolean {
    return (e as { status?: unknown } | undefined)?.status === 404;
}
